import angular from 'angular';
import { debounce, debouncedCall } from 'data/function';
import { BUILDER_GENERAL_CONFIGURATION_VARIABLE_REDO, BUILDER_GENERAL_CONFIGURATION_VARIABLE_UNDO } from '../BuilderSegmentEvents';
import { ConfigurationIAction } from '../historyActions/ConfigurationIAction';
import { ACTION_UNDO_REDO_CHANGES } from '../tabs/actions/ActionController';
import { BuilderConfigurationsSidebarFeatures } from './BuilderConfigurationsSidebarFeatures';
const builderConfigurationVariablesContext = 'builder-configuration-variables-context-activated';
const builderConfigurationVariablesSessionExpiration = 'builder-configuration-variables-sessionexpiration-edited';
const builderConfigurationVariablesActionTimeout = 'builder-configuration-variables-actiontimeout-edited';
export const builderConfigurationVariablesFlowId = 'builder-configuration-variables-flowid-reseted';
const BuilderConfigurationPrefix = 'builder:';
const MinimumIntentScoreName = `${BuilderConfigurationPrefix}minimumIntentScore`;
const StateTrackName = `${BuilderConfigurationPrefix}stateTrack`;
const StateExpirationName = `${BuilderConfigurationPrefix}stateExpiration`;
const ActionExecutionTimeoutName = `${BuilderConfigurationPrefix}actionExecutionTimeout`;
export const UseTunnelOwnerContextName = `${BuilderConfigurationPrefix}useTunnelOwnerContext`;
const OwnerIdentityName = `${BuilderConfigurationPrefix}ownerIdentity`;
const MinuteSeconds = 60;
const HourSeconds = MinuteSeconds * 60;
const DaySeconds = HourSeconds * 24;
export const CHANGED_DELAY = 1500;
export class BuilderConfigurationTypes {
}
export class BuilderConfiguration {
    static $inject = ['configuration', 'onChanged', 'UndoRedoService', '$translate', 'BuilderSegmentService', '$rootScope', '$timeout', 'secretConfiguration'];
    constructor(configuration, onChanged, UndoRedoService, $translate, BuilderSegmentService, $rootScope, $timeout, secretConfiguration) {
        this.configuration = configuration;
        this.onChanged = onChanged;
        this.UndoRedoService = UndoRedoService;
        this.$translate = $translate;
        this.BuilderSegmentService = BuilderSegmentService;
        this.$rootScope = $rootScope;
        this.$timeout = $timeout;
        this.secretConfiguration = secretConfiguration;
        this.isBuilderBlockUndoRedoConfigurationVariables = false;
        this.action = new BuilderConfigurationTypes();
        this.handleToggleAutomaticTracking = async (event) => {
            this.action.stateTrack = event.detail.checked;
            return this.synchronize();
        };
        this.handleToggleUseTunnelOwnerContext = async (event) => {
            this.action.useTunnelOwnerContext = event.detail.checked;
            return this.synchronize('builder-configuration-variables-context-activated');
        };
        this.init();
    }
    async updateViewVariables() {
        this.action.minimumIntentScore = Number.parseFloat(this.configuration[MinimumIntentScoreName]) * 100;
        if (this.configuration[StateTrackName]) {
            this.action.stateTrack = (this.configuration[StateTrackName]).toLowerCase() === 'true';
        }
        else {
            this.action.stateTrack = false;
        }
        if (this.configuration[StateExpirationName]) {
            this.action.stateExpiration = this.timeSpanToSeconds(this.configuration[StateExpirationName]) || 0;
        }
        else {
            this.action.stateExpiration = undefined;
        }
        if (this.configuration[ActionExecutionTimeoutName]) {
            this.action.actionExecutionTimeout = Number.parseFloat(this.configuration[ActionExecutionTimeoutName]) || 0;
        }
        else {
            this.action.actionExecutionTimeout = undefined;
        }
        if (this.configuration[UseTunnelOwnerContextName]) {
            this.action.useTunnelOwnerContext = this.configuration[UseTunnelOwnerContextName].toLowerCase() === 'true';
        }
        else {
            this.action.useTunnelOwnerContext = false;
        }
        this.action.ownerIdentity = this.configuration[OwnerIdentityName];
        this.action.variables = {};
        Object.keys(this.configuration).forEach((name) => {
            if (!name.startsWith('builder:')) {
                this.action.variables[name] = this.configuration[name];
            }
        });
        if (this.isBuilderSecretConfigurationEnabled) {
            this.action.secretVariables = {};
            Object.keys(this.secretConfiguration).forEach((name) => {
                this.action.secretVariables[name] = this.secretConfiguration[name];
            });
        }
    }
    async init() {
        const debouncedSynchronize = debounce(() => this.synchronize(), CHANGED_DELAY);
        await this.checkFeatures();
        await this.updateViewVariables();
        this.sliderConfig = {
            options: {
                floor: 0,
                ceil: 100,
                step: 1,
                showSelectionBar: true,
                hideLimitLabels: true,
                translate: value => value + '%',
                onChange: debouncedSynchronize.bind(this),
                onEnd: () => this.trackConfigurationVariablesAiConfiabilityDefined(),
            },
        };
        if (this.isBuilderBlockUndoRedoConfigurationVariables) {
            this.nameOfConfigurationVariables = await this.$translate('builder-configurations.variables.title');
            this.configurationProxy = this.action;
            this.UndoRedoService.unlock();
            this.$timeout(() => this.initializeProxy(), 1000);
        }
        if (this.isBuilderSecretConfigurationEnabled) {
            this.nameOfSecretConfigurationVariables = await this.$translate('builder-configurations.variables.secret.title');
        }
    }
    async checkFeatures() {
        this.isBuilderBlockUndoRedoConfigurationVariables = await BuilderConfigurationsSidebarFeatures.isConfigurationVariablesEnabled();
        this.isBuilderSecretConfigurationEnabled = await BuilderConfigurationsSidebarFeatures.isBuilderSecretConfigurationEnabled();
    }
    async synchronize(typeOfConfiguration) {
        this.configuration[MinimumIntentScoreName] = (this.action.minimumIntentScore / 100).toString();
        this.configuration[StateTrackName] = this.action.stateTrack?.toString();
        this.configuration[UseTunnelOwnerContextName] = this.action.useTunnelOwnerContext?.toString();
        this.configuration[OwnerIdentityName] = this.action.ownerIdentity;
        if (this.action.stateExpiration) {
            this.configuration[StateExpirationName] = this.secondsToTimeSpan(this.action.stateExpiration);
        }
        else {
            delete this.configuration[StateExpirationName];
        }
        if (this.action.actionExecutionTimeout) {
            this.configuration[ActionExecutionTimeoutName] = this.action.actionExecutionTimeout.toString();
        }
        else {
            delete this.configuration[ActionExecutionTimeoutName];
        }
        if (this.action.variables) {
            // Create a copy of the variables with safe names
            const variables = {};
            Object.keys(this.action.variables).forEach((name) => {
                const safeName = name.replace(/[^a-zA-Z0-9]/g, '');
                if (safeName && safeName != '') {
                    variables[safeName] = this.action.variables[name];
                }
            });
            // Remove from the configuration the values deleted from the variables
            Object.assign(this.configuration, variables);
        }
        // Remove from the configuration the values deleted from the variables
        Object.keys(this.configuration)
            .filter((name) => !name.startsWith(BuilderConfigurationPrefix) && !this.action?.variables[name])
            .forEach((name) => delete this.configuration[name]);
        if (typeOfConfiguration) {
            let payload = {};
            switch (typeOfConfiguration) {
                case builderConfigurationVariablesContext:
                    payload = {
                        UseTunnelOwnerContextName: this.configuration[UseTunnelOwnerContextName],
                    };
                    break;
                case builderConfigurationVariablesSessionExpiration:
                    payload = {
                        StateExpirationName: this.action.stateExpiration || undefined,
                    };
                    break;
                case builderConfigurationVariablesActionTimeout:
                    payload = {
                        ActionExecutionTimeoutName: this.configuration[ActionExecutionTimeoutName],
                    };
                    break;
            }
            this.BuilderSegmentService.trackVariablesAutoTracking(typeOfConfiguration, payload, true);
        }
        if (this.isBuilderSecretConfigurationEnabled) {
            if (this.action.secretVariables) {
                const secretVariables = {};
                Object.keys(this.action.secretVariables).forEach((name) => {
                    const safeName = name.replace(/[^a-zA-Z0-9]/g, '');
                    if (safeName && safeName !== '') {
                        secretVariables[safeName] = this.action.secretVariables[name];
                    }
                });
                if (!this.secretConfiguration || typeof this.secretConfiguration !== 'object') {
                    this.secretConfiguration = {};
                }
                Object.assign(this.secretConfiguration, secretVariables);
            }
            Object.keys(this.secretConfiguration)
                .filter((name) => !this.action?.secretVariables[name])
                .forEach((name) => delete this.secretConfiguration[name]);
        }
        await this.onChanged();
    }
    initializeProxy() {
        this.action = new Proxy(this.configurationProxy, {
            set: (target, key, value) => {
                if (!this.UndoRedoService.isLocked()) {
                    const oldValue = target[key];
                    const isToogleAction = typeof (value) === 'boolean';
                    debouncedCall(isToogleAction ? 0 : CHANGED_DELAY, (oldValue) => {
                        this.handleVariableConfigurationChanges(key, value, oldValue);
                    }, oldValue);
                }
                return Reflect.set(target, key, value);
            },
        });
    }
    handleVariableConfigurationChanges(type, value, oldValue) {
        if (!this.isBuilderBlockUndoRedoConfigurationVariables) {
            return;
        }
        if (angular.equals(value, oldValue)) {
            return;
        }
        const syncAndEmitConfiguration = () => {
            this.$rootScope.$emit(ACTION_UNDO_REDO_CHANGES);
            this.synchronize();
            this.UndoRedoService.unlock();
        };
        const getActionType = (value, oldValue) => {
            if (typeof (value) === 'object' && typeof (oldValue) === 'object') {
                return Object.keys(value).length > Object.keys(oldValue).length ? 'Add' : Object.keys(value).length === Object.keys(oldValue).length ? 'Edit' : 'Remove';
            }
            else {
                return 'Edit';
            }
        };
        const payload = {
            contentType: type,
            actionType: getActionType(this.action[type], oldValue),
        };
        let dataStateName = '';
        if (type === 'secretVariables') {
            dataStateName = this.nameOfSecretConfigurationVariables;
        }
        else {
            dataStateName = this.nameOfConfigurationVariables;
        }
        const configurationIAction = new ConfigurationIAction({
            execute: (data) => {
                this.action[type] = data.after;
                this.createTracking(false, payload);
                syncAndEmitConfiguration();
            },
            undo: (data) => {
                this.action[type] = data.before;
                this.createTracking(true, payload);
                syncAndEmitConfiguration();
            },
            data: {
                after: angular.copy(this.action[type]),
                before: angular.copy(oldValue),
                state: {
                    id: dataStateName,
                    $title: dataStateName,
                },
            },
        });
        this.UndoRedoService.add(configurationIAction);
    }
    secondsToTimeSpan(totalSeconds) {
        try {
            const days = Math.floor(totalSeconds / DaySeconds);
            const hours = Math.floor((totalSeconds - (days * DaySeconds)) / HourSeconds);
            const minutes = Math.floor((totalSeconds - (days * DaySeconds) - (hours * HourSeconds)) / MinuteSeconds);
            const seconds = Math.floor(totalSeconds - (days * DaySeconds) - (hours * HourSeconds) - (minutes * MinuteSeconds));
            return `${days}.${('0' + hours).slice(-2)}:${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`;
        }
        catch (e) {
            console.error('Error converting ' + totalSeconds + ' to TimeSpan', e);
            return undefined;
        }
    }
    createTracking(isUndoAction, valueToTrack) {
        const nameOfTracking = isUndoAction ? BUILDER_GENERAL_CONFIGURATION_VARIABLE_UNDO : BUILDER_GENERAL_CONFIGURATION_VARIABLE_REDO;
        const payload = valueToTrack;
        this.BuilderSegmentService.trackingGeneralConfigurationVariables(nameOfTracking, payload, true);
    }
    trackConfigurationVariablesAiConfiabilityDefined() {
        this.BuilderSegmentService.trackConfigurationVariablesAiConfiabilityDefined({
            ConfiabilityValue: this.action.minimumIntentScore,
        }, true);
    }
    timeSpanToSeconds(timeSpan) {
        try {
            const daysHoursMinutesSeconds = timeSpan.split(':');
            const daysAndHours = daysHoursMinutesSeconds[0].split('.');
            let totalMinutes = DaySeconds * Number.parseInt(daysAndHours[0]);
            totalMinutes += HourSeconds * Number.parseInt(daysAndHours[1]);
            totalMinutes += MinuteSeconds * Number.parseInt(daysHoursMinutesSeconds[1]);
            totalMinutes += Number.parseInt(daysHoursMinutesSeconds[2]);
            return totalMinutes;
        }
        catch (e) {
            console.error('Error converting ' + timeSpan + ' to seconds', e);
            return undefined;
        }
    }
}
